import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    FormControlLabel,
    Checkbox,
    Divider,
    Card,
    CardContent,
    CircularProgress,
    FormGroup,
    FormHelperText,
    ListItem,
    Alert
} from '@mui/material';
import { CardMembership, Person, Home, Phone, Info, Star, PersonalVideo, Article, Check, School } from '@mui/icons-material'; // Icons
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { TextInput, Dropdown, DateInput, Autocomplete } from 'qubCommon/Inputs';
import { Trans, useTranslation } from 'react-i18next';
import { getMunicipalities } from 'apis/controllers/person';
import { debounce } from 'lodash';
import { useQuery, useQueryClient } from 'react-query';
import { getLookups } from 'apis/controllers/backoffice';
import { createMember, getClubs, updateProfile } from 'apis/controllers/membership';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import ImagePicker from 'ui-component/image_picker/ImagePicker';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import * as yup from 'yup';
import { formatForSave } from 'utils/formatFilesForSave';
import Waiver from 'qubCommon/Other/Waiver';
import { membership } from 'apis';
import { Functions, ProjectTables } from 'enums';
import QuestionnairePart from './QuestionnairePart';
import FilesPart from './FilesPart';
import Conditional from 'common/Conditional';
import './index.css';
import { useSelector } from 'react-redux';
import CustomMaskInput from 'qubCommon/Inputs/CustomMaskInput';
function validateQuestionnaires(data) {
    return data.every((obj) =>
        obj.answers.every(
            (answerObj) =>
                Array.isArray(answerObj.answer) && answerObj.answer.filter((item) => item !== null && item !== undefined).length > 0
        )
    );
}

const ProfileForm = (props) => {
    const { personQuery, agreementsQuery, mode, setCustomDob } = props;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const fileRefs = useRef([]);
    const { projectCountryId } = useSelector((state) => state.auth);
    const [questionnaireError, setQuestionnaireError] = useState('');

    const agreements = agreementsQuery?.data || [];
    const agreementsValidation = yup.object().shape(
        agreements.reduce((schema, agreement) => {
            schema[agreement.dataKey] = yup
                .boolean()
                .oneOf([true], `You must agree to the ${agreement.description}`)
                .required(`You must agree to the ${agreement.description}`);
            return schema;
        }, {})
    );

    const yupResolver = useYupValidationResolver(
        yup.object().shape({
            firstName: yup.string().required(t('RequiredField')),
            lastName: yup.string().required(t('RequiredField')),
            dob: yup.string().required(t('RequiredField')),
            genderId: yup.number().required(t('RequiredField')),
            agreements: agreementsValidation // Ensure this properly nests inside 'agreements'
            // parentOrganizationId: yup.string().required(t('RequiredField')),
            // organizationId: yup.string().required(t('RequiredField'))
        })
    );

    const methods = useForm({
        resolver: yupResolver,
        defaultValues: {
            ...personQuery?.data?.data,
            parentOrganizationId: personQuery?.data?.data?.parentOrganizationId
                ? personQuery?.data?.data?.parentOrganizationId?.toUpperCase()
                : null,
            organizationId: personQuery?.data?.data?.organizationId
                ? {
                      id: personQuery?.data?.data?.organizationId?.toUpperCase(),
                      title: personQuery?.data?.data?.organizationTitle
                  }
                : null,
            functions: personQuery?.data?.data?.functions ?? [{ id: null, functionId: Functions.Member }],
            geolocation: personQuery?.data?.data?.geoLocationId
                ? {
                      id: personQuery?.data?.data?.geoLocationId,
                      name: personQuery?.data?.data?.geolocationTitle
                  }
                : null
        }
    });

    const [autocompleteValue, setAutoCompleteValue] = useState('');
    const [municipalityValue, setMunicipalityValue] = useState('');

    const municipalitiesQuery = useQuery({
        queryKey: ['municipalities', municipalityValue],
        queryFn: async () => {
            var res = await getMunicipalities({
                search: municipalityValue
            });
            return res;
        }
    });

    const handleMunicipalitiesAutoCompleteChange = debounce(function (value) {
        setMunicipalityValue(value);
    }, 500);

    useEffect(() => {
        if (methods.watch('countryId') !== projectCountryId) {
            methods.unregister('geolocation');
            setMunicipalityValue('');
        }
    }, [methods.watch('countryId')]);

    const organizationsQuery = useQuery('organizations', async () => {
        var res = await getLookups({ dataKey: 'OrgProvinces' });
        return res;
    });

    const countriesQuery = useQuery('countries', async () => {
        const res = await getLookups({ dataKey: 'Countries' });
        return res;
    });
    const gendersQuery = useQuery('genders', async () => {
        const res = await getLookups({ dataKey: 'Genders' });
        return res;
    });

    const functionsQuery = useQuery('functions', async () => {
        const res = await getLookups({ dataKey: 'Functions' });
        return res?.filter((f) => [200, 210, 280]?.includes(f?.EntityTypeId));
    });

    const clubsQuery = useQuery(['clubs', autocompleteValue, methods.watch('parentOrganizationId')], async () => {
        var res = await getClubs({
            search: autocompleteValue,
            parentGuid: methods.watch('parentOrganizationId')
        });
        return res;
    });

    const handleAutoCompleteChange = debounce(function (value) {
        setAutoCompleteValue(value);
    }, 500);

    const selectedFunctions = methods?.watch('functions') || [];

    const onSubmit = async (data) => {
        const completeQuestionnaire = validateQuestionnaires(data?.questionnaires?.filter(Boolean).flat());

        if (!completeQuestionnaire) {
            setQuestionnaireError(true);
            return;
        } else setQuestionnaireError(false);

        if (fileRefs.current) {
            await Promise.all(
                fileRefs.current.map(async (childRef) => {
                    if (childRef && childRef.filebundles?.files?.filter((file) => file?.id)?.map((file) => file.id)?.length > 0) {
                        await childRef.uploadFiles();
                    }
                })
            );
        }
        const files = [];
        fileRefs.current.forEach((childRef) => {
            if (childRef) {
                // childRef.saveData();
                if (childRef.filebundles?.files?.filter((file) => file?.id)?.map((file) => file.id)?.length > 0)
                    files.push({
                        personFunctionId: childRef.filebundles.personFunctionId,
                        functionId: childRef.filebundles.functionId,
                        bundles: childRef.filebundles?.files?.filter((file) => file?.id)?.map((file) => file.id) || []
                    });
            }
        });

        const funcMappedData = {
            ...data,
            functionIds: data?.functions?.map((f) => f.functionId),
            organizationId: data?.organizationId?.id ? data?.organizationId?.id : null,
            geoLocationId: data?.geolocation?.id ?? null
        };

        const agreements = data?.agreements;

        const formData = await formatForSave(
            funcMappedData,
            null,
            null,
            [],
            files,
            data?.questionnaires?.filter(Boolean).flat(),
            agreements
        );

        if (mode === 'edit') {
            try {
                const res = await updateProfile(formData);
                if (res?.status === 'success') {
                    enqueueSnackbar(res?.message, { variant: 'success' });
                    queryClient.invalidateQueries('questionnaire');
                    navigate('/home');
                }
                if (res?.status !== 'success') enqueueSnackbar(res?.message, { variant: res?.status });
            } catch (e) {
                console.log(e);
                enqueueSnackbar('Oops, something went wrong...', { variant: 'error' });
            } finally {
                queryClient.invalidateQueries('profile');
            }
        }

        if (mode === 'create') {
            try {
                const res = await createMember(formData);
                if (res?.status === 'success') {
                    enqueueSnackbar(res?.message, { variant: 'success' });
                    queryClient.invalidateQueries('questionnaire');
                    navigate('/home');
                }
                if (res?.status !== 'success') enqueueSnackbar(res?.message, { variant: res?.status });
            } catch (e) {
                console.log(e);
                enqueueSnackbar('Oops, something went wrong...', { variant: 'error' });
            } finally {
                queryClient.invalidateQueries('profile');
            }
        }
    };

    const handleAgreement = () => {
        alert('User has agreed to the terms!');
    };

    return (
        <FormProvider {...methods}>
            <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
                <Typography align="center" variant="h2" fontWeight="bold" gutterBottom>
                    {mode === 'edit'
                        ? `Profile: ${personQuery?.data?.data?.lastName} ${personQuery?.data?.data?.firstName} `
                        : t('MembershipRegistration')}
                </Typography>

                {/* MEMBERSHIP DETAILS */}
                {/* <Card elevation={3} sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CardMembership sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                <Typography variant="h4" fontWeight="bold">
                                    {t('Membership Details')}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <Dropdown
                                        name="membershipType"
                                        label={t('Type of Membership *')}
                                        fullWidth
                                        options={[
                                            { label: t('Adult'), value: 'adult' },
                                            { label: t('Youth'), value: 'youth' },
                                            { label: t('Grassroots'), value: 'grassroots' }
                                        ]}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Dropdown name="affiliateMembership" label={t('Affiliate Membership')} fullWidth options={[]} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card> */}

                {/* PERSONAL INFO */}
                <Card elevation={3} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Person sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                            <Typography variant="h4" fontWeight="bold">
                                {t('PersonalInfo')}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Grid container>
                            <Grid container item xs={12} lg={4} justifyContent="center">
                                <ImagePicker
                                    api={membership}
                                    ProjectTable={ProjectTables.Person}
                                    recordId={null}
                                    recordGuid={personQuery?.data?.data?.personId}
                                    aspect={4 / 5}
                                    editMode={true}
                                    showPairStatus={false}
                                />
                            </Grid>
                            <Grid container item xs={12} lg={8} spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextInput name="lastName" label={t('LastName')} fullWidth required defaultFromProps={false} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextInput name="firstName" label={t('FirstName')} fullWidth required defaultFromProps={false} />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <DateInput
                                        name="dob"
                                        label={t('DOB')}
                                        fullWidth
                                        required
                                        defaultFromProps={false}
                                        blurFn={(value) => {
                                            setCustomDob(value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Dropdown name="genderId" label={t('Gender')} fullWidth options={gendersQuery?.data} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Conditional condition={mode === 'edit'}>
                    {organizationsQuery?.status === 'success' && (
                        <>
                            {/* AFFILIATION INFO */}
                            <Card elevation={3} sx={{ mb: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <School sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                        <Typography variant="h4" fontWeight="bold">
                                            {t('AffiliationMembershipClub')}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />

                                    <Grid container spacing={2}>
                                        {/* Organization Selection */}
                                        <Grid item xs={12}>
                                            <Dropdown
                                                disabled={!personQuery?.data?.data?.canChangeOrganization}
                                                label={t('MembershipProvince')}
                                                fullWidth
                                                name="parentOrganizationId"
                                                changeFn={() => {
                                                    methods?.setValue('organizationId', null);
                                                    setAutoCompleteValue(null);
                                                }}
                                                options={organizationsQuery?.data}
                                            />
                                        </Grid>

                                        {/* Organization Selection */}
                                        {!personQuery?.isLoading && personQuery?.status === 'success' ? (
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    label={t('ClubSchool')}
                                                    name="organizationId"
                                                    disabled={
                                                        !methods?.watch('parentOrganizationId') ||
                                                        !personQuery?.data?.data?.canChangeOrganization
                                                    }
                                                    labelName="title"
                                                    options={clubsQuery?.data || []}
                                                    onInputChange={(_, value) => handleAutoCompleteChange(value)}
                                                />
                                            </Grid>
                                        ) : null}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </Conditional>

                {/* ADDRESS INFO */}
                <Card elevation={3} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Home sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                            <Typography variant="h4" fontWeight="bold">
                                {t('AddressInformation')}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Dropdown label={t('Country')} fullWidth name="countryId" options={countriesQuery?.data} />
                            </Grid>
                            {/* Organization Selection */}
                            {methods.watch('countryId') === projectCountryId ? (
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        label={t('Municipality')}
                                        name="geolocation"
                                        labelName="name"
                                        options={municipalitiesQuery?.data || []}
                                        onInputChange={(_, value) => handleMunicipalitiesAutoCompleteChange(value)}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12} sm={6} />
                            )}
                            <Grid item xs={12} sm={4}>
                                <TextInput label={t('Number')} fullWidth name="streetNr" defaultFromProps={false} />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextInput label={t('Street')} fullWidth name="address1" defaultFromProps={false} />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <TextInput label={t('Address2')} fullWidth name="address2" defaultFromProps={false} />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextInput label={t('ZipCode')} fullWidth name="zipCode" defaultFromProps={false} />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextInput label={t('Region')} fullWidth name="region" defaultFromProps={false} />
                            </Grid>

                            <Grid item xs={12} sm={5}>
                                <TextInput label={t('City')} fullWidth name="city" defaultFromProps={false} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* CONTACT INFO */}
                <Card elevation={3} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Phone sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                            <Typography variant="h4" fontWeight="bold">
                                {t('ContactInformation')}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                            {/* <Grid item xs={12} md={6}>
                                <CustomMaskInput name="mobile2" label={t('CellNumber')} />
                            </Grid> */}
                            <Grid item xs={12} md={6}>
                                <TextInput name="mobile" label={t('CellNumber')} fullWidth required defaultFromProps={false} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextInput name="eMail" label={t('EmailAddress')} fullWidth required defaultFromProps={false} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* PRIMARY ROLE */}
                <Card elevation={3} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Star sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                            <Typography variant="h4" fontWeight="bold">
                                {t('Roles')}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />

                        <FormGroup>
                            {functionsQuery?.data?.map((func) => (
                                <Controller
                                    key={func.value}
                                    name="functions"
                                    control={methods?.control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    disabled={func?.value === Functions.Member}
                                                    checked={
                                                        selectedFunctions.some((f) => f.functionId === func.value) ||
                                                        func.value === Functions.Member
                                                    }
                                                    onChange={(e) => {
                                                        if (!e.target.checked) {
                                                            methods.unregister(`questionnaires[${func?.value}]`);
                                                        }
                                                        const newValue = e.target.checked
                                                            ? [...selectedFunctions, { id: null, functionId: func.value }] // Add selected
                                                            : selectedFunctions.filter((f) => f.functionId !== func.value); // Remove unselected
                                                        field.onChange(newValue);
                                                    }}
                                                />
                                            }
                                            label={t(func.label)}
                                        />
                                    )}
                                />
                            ))}
                        </FormGroup>
                    </CardContent>
                </Card>

                {/* Questionnaire */}
                {selectedFunctions.map((f, index) => (
                    <QuestionnairePart func={f} arrayIndex={index} />
                ))}

                {/*Attachments */}
                {selectedFunctions.map((f, index) => (
                    <FilesPart func={f} functionsQuery={functionsQuery} key={index} ref={(el) => (fileRefs.current[index] = el)} />
                ))}
                {/* WAIVER AGREEMENT */}
                <Card
                    elevation={3}
                    sx={{
                        mb: 3,
                        p: 3,
                        bgcolor: Object.keys(methods?.formState?.errors || {}).some((key) =>
                            agreementsQuery?.data?.some((agreement) => `agreements.${agreement.dataKey}` === key)
                        )
                            ? '#ffdddd'
                            : 'null'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Article sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {t('ConsentPolicyAgreement')}
                        </Typography>
                    </Box>

                    <Typography variant="body1" color="textSecondary">
                        You must read and accept the following policies before proceeding.
                    </Typography>
                    {agreements.map((waiver) => (
                        <Waiver
                            title={waiver?.description}
                            name={`agreements.${waiver?.dataKey}`}
                            label={`By checking this box, I confirm that I have reviewed and accepted the ${waiver?.description} above.`}
                            content={<iframe src={`/${waiver?.path}`} style={{ width: '100%', height: '100%' }} />}
                            defaultValue={waiver?.defaultValue}
                        />
                    ))}

                    {/* <Waiver
                        title="Concussion Policy"
                        name="concussion"
                        label={t('Profile.ConcusiionLabel')}
                        content={t('Profile.ConcussionContent')}
                    />
                    <Waiver
                        title="Anti-Doping Policy"
                        label={t('Profile.DopingLabel')}
                        content={t('Profile.ConcusiionContent')}
                        name="antidoping"
                    /> */}
                    {agreements?.map((waiver) => (
                        <Typography hidden={!methods?.formState?.errors?.[`agreements.${waiver?.dataKey}`]} color="error" fontWeight="bold">
                            {methods?.formState?.errors?.[`agreements.${waiver?.dataKey}`]?.message}!
                        </Typography>
                    ))}
                </Card>

                <Alert severity="error" hidden={!questionnaireError} sx={{ marginBottom: 2 }}>
                    {t('QuestionnairesNotAnswered')}
                </Alert>

                {/* Submit Button */}
                <Button onClick={methods.handleSubmit(onSubmit)} variant="contained" color="primary" fullWidth size="large">
                    {mode === 'create' ? t('Create') : t('Save')}
                </Button>
            </Box>
        </FormProvider>
    );
};

export default ProfileForm;
